/**
 * Catálogo embutido de alimentos populares (PT-BR), organizado por grupo
 * alimentar — base para o cuidador montar uma refeição rápido na tela
 * "preparar refeição", sem precisar cadastrar tudo antes.
 *
 * Ao tocar um popular que ainda não existe na biblioteca, ele é criado como
 * Alimento (status "em teste") e entra na seleção. Itens já cadastrados (casados
 * por nome normalizado) são reaproveitados — nada é duplicado.
 *
 * É só uma lista de sugestões comuns, NÃO uma base nutricional. A base aberta
 * colaborativa de alimentos brasileiros é a iniciativa maior (P2.3 do escopo).
 */
import type { GrupoAlimentar } from "../types";

export interface ItemPopular {
  nome: string;
  grupo: GrupoAlimentar;
}

/** Normaliza nome p/ casar com a biblioteca (sem acento, minúsculo, sem espaços extras). */
export function normalizarNome(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Populares por grupo (≥10 cada). Nomes em minúsculo, no padrão do app. */
export const POPULARES_POR_GRUPO: Record<GrupoAlimentar, string[]> = {
  proteina: [
    "ovo",
    "frango",
    "carne moída",
    "peixe",
    "feijão",
    "lentilha",
    "grão-de-bico",
    "almôndega",
    "salsicha",
    "peito de peru",
    "atum",
    "hambúrguer",
  ],
  carboidrato: [
    "arroz",
    "macarrão",
    "pão",
    "batata",
    "batata-doce",
    "mandioca",
    "polenta",
    "tapioca",
    "aveia",
    "cuscuz",
    "milho",
    "panqueca",
  ],
  fibra: [
    "brócolis",
    "cenoura",
    "tomate",
    "abobrinha",
    "alface",
    "pepino",
    "espinafre",
    "beterraba",
    "couve-flor",
    "ervilha",
    "vagem",
    "abóbora",
  ],
  fruta: [
    "banana",
    "maçã",
    "morango",
    "uva",
    "laranja",
    "melancia",
    "mamão",
    "manga",
    "pera",
    "abacaxi",
    "melão",
    "pêssego",
  ],
  laticinio: [
    "leite",
    "iogurte",
    "queijo",
    "requeijão",
    "petit suisse",
    "queijo cottage",
    "cream cheese",
    "coalhada",
    "manteiga",
    "mussarela",
    "leite fermentado",
    "nata",
  ],
  outro: [
    "biscoito",
    "bolacha",
    "bolo",
    "gelatina",
    "suco",
    "achocolatado",
    "pipoca",
    "geleia",
    "mel",
    "granola",
    "barrinha de cereal",
    "sorvete",
  ],
};

/** Lista achatada com o grupo de cada item (útil para buscas/iteração). */
export const POPULARES: ItemPopular[] = (
  Object.entries(POPULARES_POR_GRUPO) as [GrupoAlimentar, string[]][]
).flatMap(([grupo, nomes]) => nomes.map((nome) => ({ nome, grupo })));
